<template>
  <b-modal
    id="bill-modal"
    ref="modal"
    :title="title"
    @hidden="$emit('onClose')"
    @show="onShow"
    size="lg"
  >
    <ValidationObserver
      ref="billForm"
      tag="form"
      @submit.prevent="onSubmit"
      id="billForm"
    >
      <b-form-group
        label-cols-lg="3"
        label="Kategoria:"
        label-for="bills_category_name"
        label-align-lg="right"
      >
        <ValidationProvider
          name="bills_category_name"
          rules="required"
          v-slot="validationContext"
        >
          <multiselect
            id="categories"
            placeholder
            label="name"
            track-by="id"
            :options="categories"
            :multiple="false"
            v-model="form.category"
            @tag="createCategoryTag"
            @input="selectCategory"
            :taggable="true"
            :loading="isCategoryLoading"
          ></multiselect>
          <b-form-invalid-feedback :force-show="true"
            >&nbsp;{{ validationContext.errors[0] }}</b-form-invalid-feedback
          >
        </ValidationProvider>
      </b-form-group>
      <b-form-group
        label-cols-lg="3"
        label="Podwykonawca: "
        label-for="bills_subcontractor"
        label-align-lg="right"
        v-show="isSubcontractor"
      >
        <ValidationProvider
          name="subcontractor"
          rules="required"
          v-slot="validationContext"
        >
          <multiselect
            id="bills_subcontractor"
            placeholder
            label="name"
            track-by="id"
            :options="availableSubcontractors"
            :multiple="false"
            v-model="form.subcontractor"
            :loading="isSubcontractorsLoading"
          ></multiselect>
          <b-form-invalid-feedback :force-show="true"
            >&nbsp;{{ validationContext.errors[0] }}</b-form-invalid-feedback
          >
        </ValidationProvider>
      </b-form-group>
      <b-form-group
        label-cols-lg="3"
        label="Przydziel do budów: "
        label-for="bills_construction"
        label-align-lg="right"
      >
        <ValidationProvider
          name="construction"
          rules="required"
          v-slot="validationContext"
        >
          <multiselect
            id="bills_construction"
            label="name"
            track-by="id"
            @select="formatSelectConstruction"
            :options="availableProjects"
            :multiple="true"
            v-model="form.construction"
            :loading="isProjectsLoading"
          ></multiselect>
          <b-form-invalid-feedback :force-show="true"
            >&nbsp;{{ validationContext.errors[0] }}</b-form-invalid-feedback
          >
        </ValidationProvider>
      </b-form-group>
      <div class="section-title">Dane rachunku:</div>
      <b-form-group
        label-cols-lg="3"
        label="RE Nr:"
        label-for="number"
        label-align-lg="right"
      >
        <ValidationProvider
          name="number"
          rules="required"
          v-slot="validationContext"
        >
          <b-form-input
            id="number"
            v-model="form.number"
            type="text"
          ></b-form-input>
          <b-form-invalid-feedback :force-show="true"
            >&nbsp;{{ validationContext.errors[0] }}</b-form-invalid-feedback
          >
        </ValidationProvider>
      </b-form-group>
      <!-- Data wystawienia -->
      <b-form-group
        label-cols-lg="3"
        label="Data wystawienia:"
        label-for="issue_date"
        label-align-lg="right"
      >
        <ValidationProvider
          name="issue_date"
          rules="required"
          v-slot="validationContext"
        >
          <div style="max-width: 200px">
            <v-date-picker
              v-model="form.issue_date"
              :masks="{ input: 'YYYY-MM-DD' }"
              :popover="{ visibility: 'click' }"
              :columns="$screens({ default: 1, md: 2 })"
            ></v-date-picker>
          </div>
          <b-form-invalid-feedback :force-show="true"
            >&nbsp;{{ validationContext.errors[0] }}</b-form-invalid-feedback
          >
        </ValidationProvider>
      </b-form-group>
      <!-- STAWKA VAT -->
      <b-form-group
        label-cols-lg="3"
        label="Stawka Mswt/Vat:"
        label-for="vat_rate"
        label-align-lg="right"
      >
        <ValidationProvider
          name="vat_rate"
          rules="required"
          v-slot="validationContext"
        >
          <b-input-group append="%">
            <b-form-select
              id="vat_rate"
              class="col-lg-2 text-center"
              :options="vatRates"
              v-model="form.vat_rate"
              @change="changedNetto"
            ></b-form-select>
          </b-input-group>
          <b-form-invalid-feedback :force-show="true"
            >&nbsp;{{ validationContext.errors[0] }}</b-form-invalid-feedback
          >
        </ValidationProvider>
      </b-form-group>
      <!-- KWOTA NETTO -->
      <b-form-group
        label-cols-lg="3"
        label="Kwota netto:"
        label-for="netto"
        label-align-lg="right"
      >
        <b-form inline>
          <ValidationProvider
            name="netto_amount"
            rules="required"
            v-slot="validationContext"
          >
            <b-form-input
              id="netto_amount"
              v-model.lazy="form.netto_amount"
              :formatter="formatNumber"
              @change="changedNetto"
              @blur="toRounding('netto_amount')"
              type="text"
            ></b-form-input>
            <b-form-invalid-feedback :force-show="true"
              >&nbsp;{{ validationContext.errors[0] }}</b-form-invalid-feedback
            >
          </ValidationProvider>
          <ValidationProvider
            name="currency"
            rules="required"
            v-slot="validationContext"
          >
            <b-form-select
              id="currency"
              :value="null"
              v-model="form.currency"
              :options="currency"
            ></b-form-select>
            <b-form-invalid-feedback :force-show="true"
              >&nbsp;{{ validationContext.errors[0] }}</b-form-invalid-feedback
            >
          </ValidationProvider>
        </b-form>
      </b-form-group>
      <!-- Przewalutowanie -->
      <b-form-group
        label-cols-lg="3"
        label="Pobierz kurs z dnia:"
        label-for="exchange"
        label-align-lg="right"
        v-if="form.currency !== getMainBaseCurrency"
      >
        <div class="form-inline">
          <ValidationProvider name="exchange_date" v-slot="validationContext" tag="div" class="input-group mb-2 mr-sm-2 mb-sm-0" style="max-width: 200px">
              <v-date-picker
                :max-date="new Date()"
                v-model="convert_date"
                @input="convertCurrency"
                :masks="{ input: 'YYYY-MM-DD' }"
                :popover="{ visibility: 'click' }"
                :columns="$screens({ default: 1, md: 2 })"
              ></v-date-picker>

            <b-form-invalid-feedback :force-show="true"
              >&nbsp;{{ validationContext.errors[0] }}</b-form-invalid-feedback
            >
          </ValidationProvider>
          <ValidationProvider
            name="exchange_rate"
            rules="required"
            v-slot="validationContext"
          >
            <b-input-group
              :prepend="getMainBaseCurrency +' / '+ form.currency"
              class="mb-2 mr-sm-2 mb-sm-0"
            >
              <b-form-input
                class="col-lg-9"
                id="exchange_rate"
                v-model.lazy="form.exchange_rate"
                :formatter="formatNumber"
                type="text"
                @change="cleaerCurrencyDate"
              ></b-form-input>
            </b-input-group>
            <b-form-invalid-feedback :force-show="true"
              >&nbsp;{{ validationContext.errors[0] }}</b-form-invalid-feedback
            >
          </ValidationProvider>
        </div>
      </b-form-group>
      <!-- KWOTA VAT -->
      <b-form-group
        label-cols-lg="3"
        label="Mswt/Vat:"
        label-for="vat"
        label-align-lg="right"
      >
        <ValidationProvider
          name="vat_amount"
          rules="required"
          v-slot="validationContext"
        >
          <b-input-group :prepend="form.currency">
            <b-form-input
              id="vat_amount"
              class="col-lg-5"
              v-model.lazy="form.vat_amount"
              :formatter="formatNumber"
              type="text"
              @change="finalCalc"
              @blur="toRounding('vat_amount')"
            ></b-form-input>
          </b-input-group>
          <b-form-invalid-feedback :force-show="true"
            >&nbsp;{{ validationContext.errors[0] }}</b-form-invalid-feedback
          >
        </ValidationProvider>
      </b-form-group>

      <b-form-group
        label-cols-lg="3"
        label="Rabat/Nachlass:"
        label-for="discount"
        label-align-lg="right"
      >
        <div class="form-inline">
          <ValidationProvider
            name="discount_percentage"
            rules="required"
            v-slot="validationContext"
          >
            <b-input-group prepend="%" class="mb-2 mr-sm-2 mb-sm-0">
              <b-form-input
                class="col-lg-9"
                id="discount_percentage"
                v-model.lazy="form.discount_percentage"
                :formatter="formatNumber"
                @change="discountCalc()"
                @blur="toRounding('discount_percentage')"
                type="text"
              ></b-form-input>
            </b-input-group>
            <b-form-invalid-feedback :force-show="true"
              >&nbsp;{{ validationContext.errors[0] }}</b-form-invalid-feedback
            >
          </ValidationProvider>
          <ValidationProvider
            name="discount_value"
            rules="required"
            v-slot="validationContext"
          >
            <b-input-group
              :prepend="form.currency"
              class="mb-2 mr-sm-2 mb-sm-0"
            >
              <b-form-input
                class="col-lg-9"
                id="discount_value"
                v-model.lazy="form.discount_value"
                :formatter="formatNumber"
                @change="percentCalc('discount')"
                @blur="toRounding('discount_value')"
                type="text"
              ></b-form-input>
            </b-input-group>
            <b-form-invalid-feedback :force-show="true"
              >&nbsp;{{ validationContext.errors[0] }}</b-form-invalid-feedback
            >
          </ValidationProvider>
        </div>
      </b-form-group>
      <b-form-group
        label-cols-lg="3"
        label="Kwota skonto:"
        label-for="skonto"
        label-align-lg="right"
      >
        <div class="form-inline">
          <ValidationProvider
            name="skonto_percentage"
            rules="required"
            v-slot="validationContext"
          >
            <b-input-group prepend="%" class="mb-2 mr-sm-2 mb-sm-0">
              <b-form-input
                class="col-lg-9"
                id="skonto_percentage"
                v-model.lazy="form.skonto_percentage"
                :formatter="formatNumber"
                @change="skontoCalc()"
                @blur="toRounding('skonto_percentage')"
                type="text"
              ></b-form-input>
            </b-input-group>
            <b-form-invalid-feedback :force-show="true"
              >&nbsp;{{ validationContext.errors[0] }}</b-form-invalid-feedback
            >
          </ValidationProvider>
          <ValidationProvider
            name="skonto_value"
            rules="required"
            v-slot="validationContext"
          >
            <b-input-group
              :prepend="form.currency"
              class="mb-2 mr-sm-2 mb-sm-0"
            >
              <b-form-input
                class="col-lg-9"
                id="skonto_value"
                v-model.lazy="form.skonto_value"
                :formatter="formatNumber"
                @change="percentCalc('skonto')"
                @blur="toRounding('skonto_value')"
                type="text"
              ></b-form-input>
            </b-input-group>
            <b-form-invalid-feedback :force-show="true"
              >&nbsp;{{ validationContext.errors[0] }}</b-form-invalid-feedback
            >
          </ValidationProvider>
        </div>
      </b-form-group>
      <b-form-group
        label-cols-lg="3"
        label="Suma netto:"
        label-for="final_amount_netto"
        label-align-lg="right"
      >
        <b-input-group :prepend="form.currency">
          <b-form-input
            id="final_amount_netto"
            v-model="finalAmountNetto"
            :readonly="true"
            type="text"
          ></b-form-input>
        </b-input-group>
      </b-form-group>
      <b-form-group
        label-cols-lg="3"
        label="Suma brutto:"
        label-for="final_amount"
        label-align-lg="right"
      >
        <b-input-group :prepend="form.currency">
          <b-form-input
            id="final_amount"
            v-model="form.final_amount"
            :readonly="true"
            type="text"
          ></b-form-input>
        </b-input-group>
      </b-form-group>

      <div v-if="form.construction.length > 0">
        <div class="section-title mb-0">Rozdziel płatność:</div>

        <div
          v-for="(item, index) in form.construction"
          :key="index"
          class="bills-construction"
        >
          <div class="row">
            <div class="m-3 col-lg-12" style="font-size: 16px">
              {{ item.name }}
            </div>
          </div>

          <b-form-group
            label-cols-lg="3"
            label="Kwota netto:"
            label-for="bills_construction"
            label-class="text-lg-right"
          >
            <div class="form-inline">
              <ValidationProvider
                name="kwota netto"
                rules="required"
                v-slot="validationContext"
                class="d-inline-block"
                tag="div"
              >
                <b-input-group :prepend="form.currency">
                  <b-form-input
                    class="col-lg-9"
                    :id="item.name"
                    :name="item.name"
                    v-model.lazy="item.amount"
                    :formatter="formatNumber"
                    type="text"
                  ></b-form-input>
                </b-input-group>
                <b-form-invalid-feedback :force-show="true"
                  >&nbsp;{{
                    validationContext.errors[0]
                  }}</b-form-invalid-feedback
                >
              </ValidationProvider>
              <div class="d-inline-block" style="flex: 1">
                <b-input-group class="mb-2 mr-sm-2 mb-sm-0">
                  <b-input-group-prepend is-text>
                    <i class="far fa-edit"></i>
                  </b-input-group-prepend>
                  <b-form-input
                    :id="item.name + '_note'"
                    :name="item.name + '_note'"
                    v-model.lazy="item.note"
                    placeholder="Notatka.."
                    type="text"
                  ></b-form-input>
                </b-input-group>
                <b-form-invalid-feedback :force-show="true"
                  >&nbsp;</b-form-invalid-feedback
                >
              </div>
            </div>
          </b-form-group>
        </div>
      </div>
    </ValidationObserver>
    <template v-slot:modal-footer="{ cancel }">
      <b-button size="md" variant="primary" type="submit" form="billForm"
        >Zapisz</b-button
      >
      <b-button size="md" variant="outline-danger" @click="cancel()"
        >Anuluj</b-button
      >
    </template>
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { numberFormat, zfill } from "@/services/helpers";
import BillsService from "@/services/bills.service";
import dayjs from "dayjs";

export default {
  name: "BillsModalForm",
  props: {
    bill: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },

  data() {
    return {
      vatRates: [
        { value: 0, text: "0" },
        { value: 5, text: "5" },
        { value: 8, text: "8" },
        { value: 10, text: "10" },
        { value: 16, text: "16" },
        { value: 19, text: "19" },
        { value: 23, text: "23" },
      ],
      isSubcontractor: false,
      isProjectsLoading: false,
      isCategoryLoading: false,
      isSubcontractorsLoading: false,
      form: {
        category: {
          id: null,
          name: null,
        },
        currency: "",
        discount_percentage: "0",
        discount_value: "0.00",
        final_amount: null,
        issue_date: null,
        netto_amount: null,
        number: null,
        skonto_percentage: 3,
        skonto_value: "0.00",
        vat_rate: 19,
        vat_amount: null,
        construction: [],
        subcontractor: {
          id: 0,
          name: 0,
        },
        exchange_date: "",
        exchange_rate: 0,
      },
      currency: [
        { value: "PLN", text: "PLN" },
        { value: "EUR", text: "EUR" },
      ],
      convert_date: null,
    };
  },
  computed: {
    ...mapGetters("bills", ["categories"]),
    ...mapGetters("company", ["mainCompany","getMainBaseCurrency"]),
    ...mapGetters("projectslist", ["availableProjects"]),
    ...mapGetters("subcontractors", ["availableSubcontractors"]),
    title() {
      return this.bill.id > 0 ? "Edytuj rachunek: " : "Dodaj nowy rachunek";
    },
    isEdited() {
      return this.bill.id > 0;
    },
    finalAmountNetto() {
      if (this.form.final_amount) {
        return zfill(
          parseFloat(this.form.netto_amount) -
            parseFloat(this.form.discount_value) -
            parseFloat(this.form.skonto_value)
        );
      } else {
        return "";
      }
    },
  },
  created() {},
  methods: {
    ...mapActions("bills", [
      "addCategory",
      "create",
      "update",
      "loadCategories",
    ]),
    ...mapActions("subcontractors", ["loadAvailable"]),
    ...mapActions("projectslist", ["loadProjects"]),
    getValidationState({ validated, valid = null }) {
      return validated ? valid : null;
    },
    preloading() {
      this.isProjectsLoading = true;
      this.loadProjects().then(() => {
        this.isProjectsLoading = false;
      });
      this.isCategoryLoading = true;
      this.loadCategories().then(() => {
        this.isCategoryLoading = false;
      });
      this.isSubcontractorsLoading = true;
      this.loadAvailable().then(() => {
        this.isSubcontractorsLoading = false;
      });
    },
    onShow() {
      this.preloading();
      this.form.construction = [];
      if (this.isEdited) {
        this.form.category = {
          id: 0,
          name: null,
        };
        this.form.subcontractor = {
          id: 0,
          name: null,
        };
        this.form.currency = this.bill.currency;
        this.form.discount_percentage = parseFloat(
          this.bill.discount_percentage
        ).toFixed(2);
        this.form.discount_value = this.bill.discount_value;
        this.form.final_amount = this.bill.final_amount;
        this.form.issue_date = this.bill.issue_date;
        this.form.netto_amount = this.bill.netto_amount;
        this.form.number = this.bill.number;
        this.form.skonto_percentage = parseFloat(
          this.bill.skonto_percentage
        ).toFixed(2);
        this.form.skonto_value = this.bill.skonto_value;
        this.form.vat_amount = this.bill.vat_amount;
        this.form.vat_rate = parseFloat(this.bill.vat_rate);
        this.form.issue_date = dayjs(this.bill.issue_date).toDate();
        this.form.category.name = this.bill.bills_category_name;
        this.form.category.id = this.bill.bills_category_id;
        this.convert_date = dayjs(this.bill.exchange_date).toDate();
        this.form.exchange_rate = this.bill.exchange_rate;

        if (this.form.category.id === 2) {
          this.form.subcontractor.id = this.bill.subcontractor.value;
          this.form.subcontractor.name = this.bill.subcontractor.text;
          this.isSubcontractor = true;
        } else {
          this.isSubcontractor = false;
        }
      } else {
        this.isSubcontractor = false;
        this.form.category = {
          id: null,
          name: null,
        };
        this.form.subcontractor = {
          id: 0,
          name: null,
        };
        this.form.currency = this.getMainBaseCurrency;
        this.form.discount_percentage = "0.00";
        this.form.discount_value = "0.00";
        this.form.final_amount = null;
        this.form.issue_date = null;
        this.form.netto_amount = null;
        this.form.number = null;
        this.form.skonto_percentage = parseFloat(
          this.mainCompany.options.skonto_value
        ).toFixed(2);
        this.form.skonto_value = "0.00";
        this.form.vat_amount = null;
        this.form.vat_rate = this.mainCompany.vat_rate;
        this.convert_date = null;
        this.form.exchange_rate = "";
      }
      if (this.bill.construction) {
        this.$_.each(this.bill.construction, (item) => {
          this.form.construction.push({
            id: item.value,
            name: item.text,
            amount: item.amount,
            note: item.note,
          });
        });
      }
    },
    onSubmit() {
      this.$refs.billForm.validate().then((success) => {
        if (!success) {
          return;
        }
        let data = this.copyForm();
        this.$Progress.start();
        if (this.isEdited) {
          //-- EDITED
          if (data.currency === this.getMainBaseCurrency) {
            data.exchange_date = null;
            data.exchange_rate = 1;
          }
          this.update({ billid: this.bill.id, form: data })
            .then(() => {
              this.$emit("onSubmit");
            })
            .catch((error) => {
              if (error.response.data) {
                this.$refs.billForm.setErrors(error.response.data.fields);
              }
            });
        } else {
          //-- CREATE NEW
          if (data.currency === this.getMainBaseCurrency) {
            data.exchange_date = null;
            data.exchange_rate = 1;
          }
          this.create(data)
            .then(() => {
              this.$emit("onSubmit");
            })
            .catch((error) => {
              if (error.response.data) {
                this.$refs.billForm.setErrors(error.response.data.fields);
              }
            });
        }
      });
    },
    selectCategory(value) {
      this.form.subcontractor = {
        id: null,
        name: null,
      };
      this.isSubcontractor = value.id === 2;
    },
    createCategoryTag(value) {
      this.addCategory(value).then((newCategory) => {
        this.form.category.id = newCategory.id;
        this.form.category.name = newCategory.name;
      });
    },
    formatSelectConstruction(selected) {
      selected.amount = null;
      selected.note = "";
    },
    changedNetto() {
      this.vatCalc();
      this.discountCalc();
    },
    vatCalc() {
      if (this.form.netto_amount === 0) return 0;
      this.form.vat_amount = (
        (this.form.netto_amount * parseFloat(this.form.vat_rate)) /
        100
      ).toFixed(2);
    },
    discountCalc() {
      if (this.form.discount_percentage === 0) {
        this.form.discount_value = "0.00";
      } else {
        this.form.discount_value = (
          (this.form.discount_percentage * this.form.netto_amount) /
          100
        ).toFixed(2);
      }
      this.skontoCalc();
    },
    skontoCalc() {
      if (this.form.skonto_percentage === 0) {
        this.form.skonto_value = "0.00";
      } else {
        this.form.skonto_value = (
          (this.form.skonto_percentage *
            (this.form.netto_amount - this.form.discount_value)) /
          100
        ).toFixed(2);
      }
      this.finalCalc();
    },
    percentCalc(type) {
      let percentageKey = type + "_percentage",
        valueKey = type + "_value";
      if (this.form.netto_amount === 0) {
        this.form[percentageKey] = "0.00";
      } else {
        this.form[percentageKey] = (
          (this.form[valueKey] * 100) /
          this.form.netto_amount
        ).toFixed(2);
      }
      // this.finalCalc();
      if (type === "discount") {
        this.skontoCalc();
      } else {
        this.finalCalc();
      }
    },
    finalCalc() {
      let tmp = (
        parseFloat(this.form.netto_amount) -
        parseFloat(this.form.discount_value) -
        parseFloat(this.form.skonto_value) +
        parseFloat(this.form.vat_amount)
      ).toFixed(2);
      this.form.final_amount = isNaN(tmp) ? 0 : tmp;
      if (this.form.construction.length === 1) {
        this.form.construction[0].amount = zfill(
          parseFloat(this.form.final_amount) - parseFloat(this.form.vat_amount)
        );
      }
    },
    copyForm() {
      let query = {};
      this.$_.forEach(this.form, (item, key) => {
        if (key === "category") {
          query.bills_category_id = this.form.category.id;
          return;
        }
        if (key === "issue_date") {
          query[key] = dayjs(item).format("YYYY-MM-DD");
          return;
        }
        if (key === "subcontractor" || key === "construction") {
          return;
        }
        query[key] = item;
      });

      if (query.bills_category_id === 2) {
        //Dodajemy podwykonawce
        query["subcontractor_id"] = this.form.subcontractor.id;
      }
      //Formatujemy na budowy
      query["constructions"] = [];
      this.$_.each(this.form.construction, function (items) {
        query["constructions"].push({
          construction_id: items.id,
          amount: items.amount,
          note: items.note,
        });
      });

      return query;
    },
    formatNumber: numberFormat,
    toRounding: function (inputName) {
      this.form[inputName] = zfill(this.form[inputName]);
    },
    convertCurrency() {
      if (this.convert_date === null) {
        this.form.exchange_date = null;
        return;
      } else {
        return BillsService.doConvertCurrency(
          dayjs(this.convert_date).format("YYYY-MM-DD"),
          this.form.currency.toLowerCase()
        ).then((response) => {
          this.form.exchange_rate = response.data.rate;
          this.form.exchange_date = response.data.date;
        });
      }
    },
    cleaerCurrencyDate() {
      this.convert_date = null;
    },
  },
};
</script>
